<script setup>
import _ from 'lodash'
import numeral from 'numeral'
import moment from 'moment'
import { Download, MoreHorizontal, Check, X, RotateCcw } from 'lucide-vue-next'
import { h, inject, watch, ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import DataTable from '@/components/ui/data-table/DataTableServerSide.vue'
import DataTableSortableColumnHeader from '@/components/ui/data-table/DataTableSortableColumnHeader.vue'
import ContentInfoCell from '@/partials/profiles/CreatorProfile/tables/content/ContentInfoCell.vue'
import ExternalLinkCell from '@/partials/profiles/CreatorProfile/tables/content/ExternalLinkCell.vue'
import SponsoredStatusCell from '@/partials/tables/cells/SponsoredStatusCell.vue'
import NamePlate from '@/components/plates/NamePlate.vue'
import AppIcon from '@/components/AppIcon.vue'
import ColumnDisplayManager from '@/partials/options/ColumnDisplayManager.vue'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { DataTableFilterMenu } from '@/components/ui/data-table'
import { formatWholeNumberWithUnit } from '@/utils/Utils'

const axios = inject('axios')
const filters = inject('filters')
const store = useStore()
const router = useRouter()
const route = useRoute()

const emit = defineEmits(['sponsorship-override-applied'])

const props = defineProps({
    reportId: {
        type: String,
        required: true,
    },
    platformId: {
        type: String,
        required: false,
    },
    platformIds: {
        type: Array,
        required: false,
        default: () => [],
    },
    contentType: {
        type: String,
    },
    dayRange: {
        type: String,
    },
    games: {
        type: Object,
        default: () => {},
    },
    superviewsById: {
        type: Object,
    },
    sponsoredDefault: {
        type: String,
        required: false,
    },
    availablePlatformOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    availableContentTypeOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    availableLanguageOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    availableCountryOptions: {
        type: Array,
        required: false,
        default: () => [],
    },
    reportStartDate: {
        type: String,
        required: false,
        default: null,
    },
    reportEndDate: {
        type: String,
        required: false,
        default: null,
    },
    reportTitle: {
        type: String,
        required: false,
    },
})

// Row selection state
const selectedRows = ref({})
const isUpdatingSponsorship = ref(false)

// Computed for selected content IDs
const selectedContentIds = computed(() => {
    return Object.keys(selectedRows.value).filter(key => selectedRows.value[key])
        .map(index => data.value[parseInt(index)]?.id)
        .filter(id => id)
})

const selectedCount = computed(() => selectedContentIds.value.length)

// Functions for row selection
const toggleRowSelection = (index) => {
    selectedRows.value = {
        ...selectedRows.value,
        [index]: !selectedRows.value[index]
    }
}

const toggleAllRows = (checked) => {
    if (checked) {
        const newSelection = {}
        data.value.forEach((_, index) => {
            newSelection[index] = true
        })
        selectedRows.value = newSelection
    } else {
        selectedRows.value = {}
    }
}

const isAllSelected = computed(() => {
    if (data.value.length === 0) return false
    return data.value.every((_, index) => selectedRows.value[index])
})

const isSomeSelected = computed(() => {
    return Object.values(selectedRows.value).some(v => v) && !isAllSelected.value
})

// Bulk sponsorship override actions
const updateSponsorshipOverride = async (override) => {
    if (selectedContentIds.value.length === 0) return

    isUpdatingSponsorship.value = true
    try {
        await axios.patch(`/reports/${props.reportId}/content/sponsorship`, {
            content_ids: selectedContentIds.value,
            override: override,
        })

        // Clear selection after successful update
        selectedRows.value = {}

        // Refresh the content table
        await fetchContent()

        // Emit event so parent can show refresh aggregations button
        emit('sponsorship-override-applied')
    } catch (error) {
        console.error('Error updating sponsorship override:', error)
    } finally {
        isUpdatingSponsorship.value = false
    }
}

// Platform filtering
const availablePlatforms = computed(() => {
    if (props.platformIds && props.platformIds.length > 0) {
        return props.platformIds
    }
    return props.platformId ? [props.platformId] : []
})

const effectivePlatformId = computed(() => {
    if (props.platformIds && props.platformIds.length > 0) {
        return props.platformIds.length === 1 ? props.platformIds[0] : null
    }
    return props.platformId
})

// Helper function to check if a column should be enabled for a platform (supports multi-platform)
const isColumnEnabledForPlatform = (platforms) => {
    if (availablePlatforms.value.length > 1) {
        // Multi-platform: enable if ANY selected platform supports it
        return availablePlatforms.value.some(pid => platforms.includes(pid))
    }
    // Single platform: check if the platform is in the list
    const platformToCheck = effectivePlatformId.value || props.platformId
    return platforms.includes(platformToCheck)
}

// Column groups for ColumnDisplayManager
const columnGroups = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'timing', label: 'Timing' },
    { id: 'metrics', label: 'Metrics' },
    { id: 'engagement', label: 'Engagement' },
]

// Helper function to render metrics - shows "—" for null/undefined (metric doesn't exist for platform)
const renderMetric = (value, format = '0,0') => {
    if (value === null || value === undefined) {
        return h('div', { class: 'text-slate-400' }, '—')
    }
    return h('div', numeral(value).format(format))
}

const renderEngagementRate = (value) => {
    if (value === null || value === undefined) {
        return h('div', { class: 'text-slate-400' }, '—')
    }
    return h('div', numeral(value).format('0.0%'))
}

// Helper function to render metrics that aren't available for Instagram stories
const renderMetricForStories = (row, value, format = '0,0') => {
    // Show — for Instagram stories since they don't have comments, likes, or meaningful engagement rates
    const rowPlatformId = row.original.platform_id || effectivePlatformId.value
    if (rowPlatformId === 'instagram' && row.original.content_sub_type === 'story') {
        return h('div', { class: 'text-slate-400' }, '—')
    }

    if (value === null || value === undefined) {
        return h('div', { class: 'text-slate-400' }, '—')
    }

    return h('div', numeral(value).format(format))
}

// All columns definition (unfiltered - used for ColumnDisplayManager)
const allColumns = computed(() => [
    {
        id: 'select',
        meta: {
            title: '',
            group: 'basic',
            label: 'Select',
        },
        header: () => h('input', {
            type: 'checkbox',
            checked: isAllSelected.value,
            indeterminate: isSomeSelected.value,
            class: 'form-checkbox translate-y-0.5',
            onChange: () => toggleAllRows(!isAllSelected.value),
        }),
        cell: ({ row }) => h('input', {
            type: 'checkbox',
            checked: selectedRows.value[row.index] || false,
            class: 'form-checkbox translate-y-0.5',
            onChange: () => toggleRowSelection(row.index),
            onClick: (e) => e.stopPropagation(),
        }),
        enableSorting: false,
        is_exportable: false,
        isEnabled: () => true,
        isSelectionColumn: true,
    },
    {
        id: 'platform_id',
        accessorFn: (row) => row.platform_id,
        meta: {
            title: 'Platform',
            group: 'basic',
            label: 'Platform',
            tooltip: { title: 'Platform', content: [{ text: 'The platform where this content was published.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const platformId = row.getValue(column.id)
            const platformName = platformId === 'twitter' ? 'X' : platformId
            return h('div', { class: 'flex items-center gap-2' }, [
                h(AppIcon, {
                    class: platformId === 'twitter' ? 'h-4 w-4 text-x' : `h-4 w-4 text-${platformId}`,
                    name: platformId === 'twitter' ? 'x-twitter' : platformId
                }),
                h('span', { class: platformId === 'twitter' ? '' : 'capitalize' }, platformName)
            ])
        },
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => availablePlatforms.value.length > 1,
        exportTransformer: (item) => ({
            Platform: _.get(item, 'platform_id', ''),
        }),
    },
    // Hidden column for content type filtering only
    {
        id: 'content_types',
        accessorFn: (row) => row.content_type,
        header: () => null,
        cell: () => null,
        enableHiding: false,
        enableSorting: false,
        is_exportable: false,
        isEnabled: () => false,
        filterFn: 'arrIncludesSome',
        meta: { isFilterOnly: true },
    },
    {
        id: 'title',
        key: 'title',
        meta: {
            title: 'Title',
            group: 'basic',
            label: 'Title',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row }) => {
            return h(ContentInfoCell, {
                data: row.original,
                platformId: row.original.platform_id || effectivePlatformId.value || props.platformId,
                contentType: props.contentType,
                row,
                isNormalized: true,
            })
        },
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            Title: _.get(item, 'title'),
        }),
        isEnabled: () => true,
    },
    {
        id: 'display_name',
        accessorFn: (row) => row.superview,
        meta: {
            title: 'Creator',
            group: 'basic',
            label: 'Creator',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const superview = row.getValue(column.id)

            return h(NamePlate, {
                logo: superview?.normalized?.avatar_url,
                name: superview?.normalized?.name,
                displayName: superview?.normalized?.display_name,
                platformId: superview?.platform_id,
                platformUserId: superview?._id,
                isDrawerEnabled: true,
            })
        },
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            Name: _.get(item, 'superview.normalized.name'),
        }),
        isEnabled: () => true,
    },
    {
        id: 'superview.normalized.followers',
        accessorFn: (row) => row?.superview?.normalized?.followers,
        meta: {
            title: 'Followers',
            group: 'metrics',
            label: 'Followers',
            tooltip: {
                title: 'Followers',
                content: [{ text: 'The channel\'s current number of followers.' }],
            },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            Followers: _.get(item, 'superview.normalized.followers') != null
                ? numeral(_.get(item, 'superview.normalized.followers')).format('0,0')
                : '',
        }),
        isEnabled: () => true,
    },
    {
        id: 'creator_language',
        accessorFn: (row) => row.creator_language,
        meta: {
            title: 'Language',
            group: 'basic',
            label: 'Language',
            tooltip: { title: 'Language', content: [{ text: 'The primary language of the creator.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (!value) return h('div', { class: 'text-muted-foreground' }, '—')
            return h('div', { class: 'uppercase text-xs font-medium' }, store.getters.getLanguageFromCode(value))
        },
        enableSorting: true,
        is_exportable: true,
        filterFn: 'arrIncludesSome',
        exportTransformer: (item) => ({
            Language: _.get(item, 'creator_language', '') ? store.getters.getLanguageFromCode(_.get(item, 'creator_language', '')) : '',
        }),
        isEnabled: () => true,
    },
    {
        id: 'creator_country',
        accessorFn: (row) => row.creator_country,
        meta: {
            title: 'Country',
            group: 'basic',
            label: 'Country',
            tooltip: { title: 'Country', content: [{ text: 'The country of the creator.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (!value) return h('div', { class: 'text-muted-foreground' }, '—')
            return h('div', { class: 'uppercase text-xs font-medium' }, store.getters.getCountryFromCode(value))
        },
        enableSorting: true,
        is_exportable: true,
        filterFn: 'arrIncludesSome',
        exportTransformer: (item) => ({
            Country: _.get(item, 'creator_country', '') ? store.getters.getCountryFromCode(_.get(item, 'creator_country', '')) : '',
        }),
        isEnabled: () => true,
    },
    {
        id: 'link',
        accessorFn: (row) => row?.link,
        meta: {
            title: 'Link',
            group: 'basic',
            label: 'Link',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            return h(ExternalLinkCell, {
                link: row.getValue(column.id),
            })
        },
        enableSorting: false,
        is_exportable: true,
        exportTransformer: (item) => ({
            Link: _.get(item, 'link'),
        }),
        isEnabled: () => isColumnEnabledForPlatform(['youtube', 'tiktok', 'twitter', 'instagram']),
    },
    {
        id: 'content_sub_type',
        accessorFn: (row) => row?.content_sub_type || row?.content_type,
        meta: {
            title: 'Type',
            group: 'basic',
            label: 'Content Type',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (!value) return h('div', '—')
            return h('div', { class: 'whitespace-nowrap capitalize' }, value)
        },
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            Type: _.get(item, 'content_sub_type') || _.get(item, 'content_type') || '',
        }),
        isEnabled: () => isColumnEnabledForPlatform(['instagram']),
    },
    {
        id: 'published_at',
        accessorFn: (row) => row?.published_at,
        meta: {
            title: 'Posted At',
            group: 'timing',
            label: 'Posted At',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (!value) return h('div', { class: 'text-slate-400' }, '—')
            return h(
                'div',
                { class: 'whitespace-nowrap' },
                moment(value).format('MMM Do, YY @ h:mm A'),
            )
        },
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => props.contentType !== 'stream',
        exportTransformer: (item) => ({
            'Posted At': item.published_at ? moment(item.published_at).format('YYYY-MM-DD hh:mm:ss') : ''
        })
    },
    {
        id: 'stream.started_at',
        key: 'stream.started_at',
        meta: {
            title: 'Started At',
            group: 'timing',
            label: 'Stream Started At',
        },
        accessorFn: (row) => row?.stream?.started_at,
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (!value) return h('div', { class: 'text-slate-400' }, '—')
            return h(
                'div',
                { class: 'whitespace-nowrap' },
                moment(value).format('MMM Do, YY @ h:mm A'),
            )
        },
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => isColumnEnabledForPlatform(['twitch', 'youtube']),
        exportTransformer: (item) => ({
            'Started At': item?.stream?.started_at ? moment(item.stream.started_at).format('YYYY-MM-DD hh:mm:ss') : ''
        })
    },
    {
        id: 'stream.ended_at',
        key: 'stream.ended_at',
        meta: {
            title: 'Ended At',
            group: 'timing',
            label: 'Stream Ended At',
        },
        accessorFn: (row) => row?.stream?.ended_at,
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (!value) return h('div', { class: 'text-slate-400' }, '—')
            return h(
                'div',
                { class: 'whitespace-nowrap' },
                moment(value).format('MMM Do, YY @ h:mm A'),
            )
        },
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => isColumnEnabledForPlatform(['twitch', 'youtube']),
        exportTransformer: (item) => ({
            'Ended At': item?.stream?.ended_at ? moment(item.stream.ended_at).format('YYYY-MM-DD hh:mm:ss') : ''
        })
    },
    {
        id: 'duration_seconds',
        accessorFn: (row) => row.duration_seconds * 1000,
        meta: {
            title: effectivePlatformId.value === 'twitch' ? 'Hours Streamed' : 'Duration',
            group: 'timing',
            label: effectivePlatformId.value === 'twitch' ? 'Hours Streamed' : 'Duration',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            if (!row.getValue(column.id)) return h('div', '---')
            return h('div', filters.humanizeDurationShort(row.getValue(column.id)))
        },
        exportTransformer: (item) => ({
            [effectivePlatformId.value === 'twitch' ? 'Hours Streamed' : 'Duration']: formatWholeNumberWithUnit(_.get(item, 'duration_seconds', 0) / 3600),
        }),
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => isColumnEnabledForPlatform(['twitch', 'youtube', 'tiktok', 'instagram']),
    },
    {
        id: 'statistics.avg_viewers',
        accessorFn: (row) => row?.statistics?.avg_viewers,
        meta: {
            sortKey: 'statistics.avg_viewers',
            title: 'Avg Viewers',
            group: 'metrics',
            label: 'Avg Viewers',
            tooltip: { title: 'Average Concurrent Viewers', content: [{ text: 'Average viewers during the stream.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Avg Viewers']: _.get(item, 'statistics.avg_viewers'),
        }),
        isEnabled: () => isColumnEnabledForPlatform(['twitch']),
    },
    {
        id: 'statistics.peak_viewers.value',
        accessorFn: (row) => row?.statistics?.peak_viewers?.value,
        meta: {
            title: 'Peak Viewers',
            group: 'metrics',
            label: 'Peak Viewers',
            tooltip: { title: 'Peak Concurrent Viewers', content: [{ text: 'Maximum viewers at any point during the stream.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Peak Viewers']: _.get(item, 'statistics.peak_viewers.value'),
        }),
        isEnabled: () => isColumnEnabledForPlatform(['twitch']),
    },
    {
        id: 'statistics.minutes_watched',
        accessorFn: (row) => row.statistics?.minutes_watched != null ? row.statistics.minutes_watched / 60 : null,
        meta: {
            title: 'Hours Watched',
            group: 'metrics',
            label: 'Hours Watched',
            tooltip: { title: 'Total Hours Watched', content: [{ text: 'Total viewing time across all viewers.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Hours Watched']: formatWholeNumberWithUnit(_.get(item, 'statistics.minutes_watched', 0) / 60),
        }),
        isEnabled: () => isColumnEnabledForPlatform(['twitch']),
    },
    {
        id: 'statistics.views',
        accessorFn: (row) => row?.statistics?.views,
        meta: {
            title: 'Views',
            group: 'metrics',
            label: 'Views',
            tooltip: { title: 'Total Views', content: [{ text: 'Total number of views for this content.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Views']: _.get(item, 'statistics.views'),
        }),
        isEnabled: () => true,
    },
    {
        id: 'statistics.avg_commenters',
        accessorFn: (row) => row?.statistics?.avg_commenters,
        meta: {
            title: 'Avg Chatters',
            group: 'engagement',
            label: 'Avg Chatters',
            tooltip: { title: 'Average Chatters', content: [{ text: 'Average number of unique chatters during the stream.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Avg Chatters']: _.get(item, 'statistics.avg_commenter'),
        }),
        isEnabled: () => isColumnEnabledForPlatform(['twitch']),
    },
    {
        id: 'statistics.avg_comments',
        accessorFn: (row) => row?.statistics?.avg_comments,
        meta: {
            title: 'Avg Comments',
            group: 'engagement',
            label: 'Avg Comments',
            tooltip: { title: 'Average Messages', content: [{ text: 'Average chat messages per minute.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Avg Comments']: _.get(item, 'statistics.avg_comments'),
        }),
        isEnabled: () => isColumnEnabledForPlatform(['twitch']),
    },
    {
        id: 'statistics.comments',
        accessorFn: (row) => row?.statistics?.comments,
        meta: {
            title: 'Comments',
            group: 'engagement',
            label: 'Comments',
            tooltip: { title: 'Total Comments', content: [{ text: 'Total number of comments on this content.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetricForStories(row, row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Comments']: _.get(item, 'statistics.comments'),
        }),
        isEnabled: () => {
            // Hide this column only if ALL content in the current view is Instagram stories
            if (effectivePlatformId.value === 'instagram' && props.contentType === 'story') {
                return false
            }
            return isColumnEnabledForPlatform(['twitch', 'youtube', 'twitter', 'instagram'])
        },
    },
    {
        id: 'statistics.likes',
        accessorFn: (row) => row?.statistics?.likes,
        meta: {
            title: 'Likes',
            group: 'engagement',
            label: 'Likes',
            tooltip: { title: 'Total Likes', content: [{ text: 'Total number of likes on this content.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetricForStories(row, row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Likes']: _.get(item, 'statistics.likes'),
        }),
        isEnabled: () => {
            // Hide this column only if ALL content in the current view is Instagram stories
            if (effectivePlatformId.value === 'instagram' && props.contentType === 'story') {
                return false
            }
            return isColumnEnabledForPlatform(['youtube', 'tiktok', 'twitter', 'instagram'])
        },
    },
    {
        id: 'statistics.shares',
        accessorFn: (row) => row?.statistics?.shares,
        meta: {
            title: effectivePlatformId.value === 'twitter' ? 'Reposts' : 'Shares',
            group: 'engagement',
            label: effectivePlatformId.value === 'twitter' ? 'Reposts' : 'Shares',
            tooltip: effectivePlatformId.value === 'twitter'
                ? { title: 'Total Reposts', content: [{ text: 'Total number of shares of this content.' }] }
                : { title: 'Total Shares', content: [{ text: 'Total number of shares on this content.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetricForStories(row, row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            [_.get(item, 'platform_id') === 'twitter' ? 'Reposts' : 'Shares']: _.get(item, 'statistics.shares'),
        }),
        isEnabled: () => {
            return isColumnEnabledForPlatform(['tiktok', 'twitter'])
        },
    },
    {
        id: 'statistics.quotes',
        accessorFn: (row) => row?.statistics?.quotes,
        meta: {
            title: 'Quotes',
            group: 'engagement',
            label: 'Quotes',
            tooltip: { title: 'Total Quotes', content: [{ text: 'Total number of quotes on this content.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderMetric(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Quotes']: _.get(item, 'statistics.quotes'),
        }),
        isEnabled: () => isColumnEnabledForPlatform(['twitter']),
    },
    {
        id: 'statistics.engagement_rate',
        accessorFn: (row) => row?.statistics?.engagement_rate,
        meta: {
            title: 'Engagement Rate',
            group: 'engagement',
            label: 'Engagement Rate',
            tooltip: { title: 'Engagement Rate', content: [{ text: 'Percentage of viewers who engaged with the content.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => renderEngagementRate(row.getValue(column.id)),
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => {
            // Hide this column only if ALL content in the current view is Instagram stories
            if (effectivePlatformId.value === 'instagram' && props.contentType === 'story') {
                return false
            }
            return true
        },
        exportTransformer: (item) => ({
            'Engagement Rate': _.get(item, 'statistics.engagement_rate') != null
                ? numeral(_.get(item, 'statistics.engagement_rate')).format('0.0%')
                : '',
        }),
    },

    {
        id : 'vod.views',
        accessorFn: (row) => row?.vod?.views ?? '',
        meta: {
            title: 'Vod Views',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {

            let formattedValue = ''
            if (row.getValue(column.id)) {
                formattedValue = numeral(row.getValue(column.id)).format('0,0')
            }
            return h('div', formattedValue)
        },
        enableSorting: false,
        isEnabled: () => {
            return props.platformId === 'twitch'
        },
        is_exportable: true,
         exportTransformer: (item) => ({
            'Vod Views': _.get(item, 'vod.views', ''),
        }),
    },

    {
        id : 'vod.url',
        accessorFn: (row) => row?.vod?.url ?? '',
        meta: {
            title: 'Vod',
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            if (row.getValue(column.id)) {
                return h(ExternalLinkCell, {
                    link: row.getValue(column.id),
                })
            }
            return h('div', '-')
        },
        enableSorting: false,
        isEnabled: () => {
            return props.platformId === 'twitch'
        },
        is_exportable: true,
        exportTransformer: (item) => ({
            'Vod': _.get(item, 'vod.url', ''),
        }),
    },

    {
        id: 'is_sponsored',
        accessorFn: (row) => row?.is_sponsored,
        meta: {
            title: 'Sponsored Status',
            group: 'basic',
            label: 'Sponsored Status',
            tooltip: { title: 'Sponsored Status', content: [{ text: 'Whether this content is sponsored or organic.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row }) => {
            return h(SponsoredStatusCell, {
                isSponsored: row.original.is_sponsored ?? false,
                analysis: row.original._analysis ?? null,
                isSponsorshipOverridden: row.original.is_sponsorship_overridden ?? false,
            })
        },
        enableSorting: true,
        is_exportable: true,
        exportTransformer: (item) => ({
            ['Sponsored Status']: _.get(item, 'is_sponsored') === true ? 'Sponsored' : 'Organic',
        }),
        isEnabled: () => true,
    },
    {
        id: 'estimated_rate',
        accessorFn: (row) => row?.estimated_rate,
        meta: {
            title: 'Content Value',
            group: 'metrics',
            label: 'Content Value',
            tooltip: { title: 'Content Value', content: [{ text: 'Estimated advertising value of this content.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (value === null || value === undefined) {
                return h('div', { class: 'text-slate-400' }, '—')
            }
            return h('div', `$${numeral(value).format('0,0')}`)
        },
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => true,
        exportTransformer: (item) => ({
            'Content Value (USD)': _.get(item, 'estimated_rate') != null
                ? numeral(_.get(item, 'estimated_rate')).format('0,0')
                : '',
        }),
    },
    {
        id: 'performance',
        accessorFn: (row) => row?.performance,
        meta: {
            title: 'Performance',
            group: 'metrics',
            label: 'Performance',
            tooltip: { title: 'Performance vs Average', content: [{ text: 'How this content performed compared to the creator\'s 90-day average. Positive means above average, negative means below.' }] },
        },
        header: ({ column }) =>
            h(DataTableSortableColumnHeader, { column, title: column.columnDef.meta.title }),
        cell: ({ row, column }) => {
            const value = row.getValue(column.id)
            if (value === null || value === undefined) {
                return h('div', { class: 'text-slate-400' }, '—')
            }
            const isPositive = value >= 0
            const arrowIcon = h(AppIcon, {
                name: isPositive ? 'arrow-trend-up' : 'arrow-trend-down',
                class: 'h-3 w-3'
            })
            const badgeClasses = isPositive
                ? 'bg-emerald-500 text-white'
                : 'bg-rose-500 text-white'
            return h('div', { class: 'flex items-center justify-start' }, [
                h('span', {
                    class: `inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium tabular-nums text-xs ${badgeClasses}`
                }, [
                    arrowIcon,
                    numeral(Math.abs(value)).format('0.0%')
                ])
            ])
        },
        enableSorting: true,
        is_exportable: true,
        isEnabled: () => true,
        exportTransformer: (item) => ({
            'Performance': _.get(item, 'performance') != null
                ? numeral(_.get(item, 'performance')).format('+0.0%')
                : '',
        }),
    },
])

// Columns filtered by platform/content type availability
const platformColumns = computed(() => {
    return allColumns.value.filter((column) => (column?.isEnabled ? column.isEnabled() : true))
})

// Columns for display manager (excludes selection column)
const displayManagerColumns = computed(() => {
    return platformColumns.value.filter((column) => !column.isSelectionColumn)
})

// Enabled columns state (for ColumnDisplayManager)
const enabledColumns = ref([])

// Initialize enabled columns from preferences or defaults
const initEnabledColumns = () => {
    const platformKey = effectivePlatformId.value || 'multi-platform'
    const savedColumns = _.get(store.state, `preferences.tables.reportContent.${platformKey}.enabledColumns`)
    if (savedColumns && savedColumns.length > 0) {
        // Filter to only include columns that are currently available
        enabledColumns.value = savedColumns.filter(id => platformColumns.value.some(col => col.id === id))
    } else {
        // Default: enable all available columns
        enabledColumns.value = platformColumns.value.map(col => col.id)
    }
}

// Transform columns for ColumnDisplayManager format (excludes selection column)
const managerColumns = computed(() => {
    return displayManagerColumns.value.map(col => ({
        id: col.id,
        group: col.meta?.group || 'basic',
        header: {
            label: col.meta?.label || col.meta?.title || col.id,
            tooltip: col.meta?.tooltip,
        },
    }))
})

// Handle enabled columns change from ColumnDisplayManager
const onEnabledColumnChange = (newEnabledColumns) => {
    enabledColumns.value = newEnabledColumns
    const platformKey = effectivePlatformId.value || 'multi-platform'
    store.commit('preferences:set', {
        key: `tables.reportContent.${platformKey}.enabledColumns`,
        value: newEnabledColumns,
    })
}

// Final columns to display (filtered by enabledColumns, always include selection column)
const columns = computed(() => {
    const selectionColumn = platformColumns.value.find(col => col.isSelectionColumn)
    // Hidden filter columns (not displayed, but needed for filter state management)
    const filterColumns = platformColumns.value.filter(col =>
        ['content_types'].includes(col.id)
    )
    const otherColumns = enabledColumns.value.length === 0
        ? displayManagerColumns.value
        : enabledColumns.value.map(id => displayManagerColumns.value.find(col => col.id === id)).filter(Boolean)

    return [
        ...(selectionColumn ? [selectionColumn] : []),
        ...filterColumns,
        ...otherColumns
    ]
})

const toolbar = ref({
    placeholder: 'Filter by title...',
    columnToFilter: 'title',
    filterValue: '',
})

const rowCount = ref(-1)

const getPreferences = () => {
    const platformKey = effectivePlatformId.value || 'multi-platform'
    return _.get(store, `state.preferences.tables.reportContent.${platformKey}`)
}

const pagination = ref({
    pageIndex: 0,
    pageSize: getPreferences()?.paging?.pageSize ? getPreferences().paging.pageSize : 10,
})

const sorting = ref(
    getPreferences()?.sorting
        ? getPreferences().sorting
        : [
              {
                  id: 'published_at',
                  desc: true,
              },
          ],
)

const filtering = ref([])
const publishDateRange = ref({ start: null, end: null })
const data = ref([])
const isLoaded = ref(false)

const contentFilterOptions = ref({ languages: [], countries: [] })

// Create filter values for API request - pass through as-is
// Backend accepts 'organic' and 'sponsored' string values for is_sponsored filter
const createToolbarFilterValues = () => {
    const mappedFilterValues = _.filter(filtering.value, item => {
            return !props.sponsoredDefault || props.sponsoredDefault === 'all' || item.id !== 'is_sponsored'
        }).map(({ id, value }) => [id, value])

    if (props.sponsoredDefault && props.sponsoredDefault !== 'all') {
        mappedFilterValues.push(['is_sponsored', [props.sponsoredDefault]])
    }

    // Add publish date filter if set
    if (publishDateRange.value.start && publishDateRange.value.end) {
        mappedFilterValues.push(['published_at', publishDateRange.value])
    }

    return mappedFilterValues
}

// Handle publish date change
const handlePublishDateChange = (dateRange) => {
    publishDateRange.value = dateRange
    pagination.value.pageIndex = 0
    fetchContent()
}

const fetchContent = async () => {
    isLoaded.value = false

    store.dispatch('CANCEL_PENDING_REQUESTS_BY_REGEX', '^/report/[^/]+$/content')

    try {

        const paging = {
            perPage: pagination.value.pageSize,
            currentPage: pagination.value.pageIndex + 1,
            sort: [],
            filters: [],
            totalRecords: 0,
        }

        if (sorting.value?.[0]?.id) {
            paging.sort.push(
                {
                    field: sorting.value[0].id,
                    direction: sorting.value?.[0]?.desc === true ? 'desc' : 'asc',
                    source: 'default',
                }
            )
        }

        paging.filters.push({ id : 'content_type', value : props.contentType })
        paging.filters.push({ id : 'day_range', value : props.dayRange })

        if (toolbar.value.filterValue) {
            paging.filters.push({ id : 'text', value : toolbar.value.filterValue })
        }

        _.forOwn(Object.fromEntries(createToolbarFilterValues()), (value, key) => {
            paging.filters.push({ id : key, value : value })
        })

        // Build request payload with facet filtering
        const requestPayload = {
            paging: paging,
            isExport: false,
        }

        const response = await axios.post(`/reports/${props.reportId}/content`, requestPayload)

        let content = response.data?.payload?.content

        if (content && response.data?.payload?.superviews) {
            const superviewsById = _.keyBy(response.data.payload.superviews, '_id')
            content = _.map(content, (item) => {
                item.superview = superviewsById[item.platform_user_id]
                return item
            })
        }

        data.value = content
        rowCount.value = response.data.payload.paging.totalRecords
        isLoaded.value = true

        if (response.data.payload.filterOptions) {
            contentFilterOptions.value = response.data.payload.filterOptions
        }
    } catch (error) {
        console.error('Error fetching report content:', error)
        data.value = []
        isLoaded.value = true
    }
}

const onExportContent = async () => {

    let fileName = `Content report - ${effectivePlatformId.value || 'multi-platform'}`
    if (props.contentType) {
        fileName += ` - ${props.contentType}s`
    }

    if (props.reportTitle) {
        fileName = `Content report - ${props.reportTitle}`.replace(/\//g, '').replace(/[^a-zA-Z0-9\s-_/\//]/g, '')
    }

    store.commit('modal:setData', {
        modal_id: 'export_table',
        data: {
            is_open: true,
            file_name: fileName,
            columns: {
                all: platformColumns.value.filter(c => c.is_exportable !== false),
                enabled: columns.value.filter(c => c.is_exportable !== false),
            },
            checkerFn: async () => {
                return []
            },
            rowCount: rowCount.value,
            providerFn: async () => {
                const reportResponse = await getExportContent()
                if (reportResponse?.isQuotaError) {
                    this.$errorHandler(reportResponse.error)
                    return -1
                }

                let content = reportResponse.data?.payload?.content

                if (content && reportResponse.data?.payload?.superviews) {
                    const superviewsById = _.keyBy(reportResponse.data.payload.superviews, '_id')
                    content = _.map(content, (item) => {
                        item.superview = superviewsById[item.platform_user_id]
                        return item
                    })
                }
                return content
            },
        },
    })
}

const getExportContent = async () => {
    const paging = {
        perPage: 10000,
        currentPage: 1,
        sort: [],
        filters: [],
        totalRecords: 0,
    }

    if (sorting.value?.[0]?.id) {
        paging.sort.push(
            {
                field: sorting.value[0].id,
                direction: sorting.value?.[0]?.desc === true ? 'desc' : 'asc',
                source: 'default',
            }
        )
    }

        paging.filters.push({ id : 'content_type', value : props.contentType })
        paging.filters.push({ id : 'day_range', value : props.dayRange })

        if (toolbar.value.filterValue) {
            paging.filters.push({ id : 'text', value : toolbar.value.filterValue })
        }

    _.forOwn(Object.fromEntries(createToolbarFilterValues()), (value, key) => {
        paging.filters.push({ id : key, value : value })
    })

    // Build request payload
    const requestPayload = {
        paging: paging,
        isExport: true,
    }

    return await axios.post(`/reports/${props.reportId}/content`, requestPayload)
}

const filterFields = computed(() => {
    const fields = []

    // Platform filter (only show if multiple platforms available)
    if (props.availablePlatformOptions.length > 1) {
        fields.push({
            label: 'Platform',
            value: 'platform_id',
            options: props.availablePlatformOptions.map(option => ({
                label: option.label,
                value: option.value,
            })),
        })
    }

    // Content Type filter (only show if multiple content types available)
    if (props.availableContentTypeOptions.length > 1) {
        fields.push({
            label: 'Content Type',
            value: 'content_types',
            options: props.availableContentTypeOptions.map(option => ({
                label: option.label,
                value: option.value,
            })),
        })
    }

    // Sponsored status filter (always show)
    fields.push({
        label: 'Sponsored Status',
        value: 'is_sponsored',
        options: [{
            label: 'Organic',
            value: 'organic',
        }, {
            label: 'Sponsored',
            value: 'sponsored',
        }],
    })

    const languageOptions = contentFilterOptions.value.languages
        .map(code => ({ value: code, label: store.getters.getLanguageFromCode(code) }))
        .sort((a, b) => a.label.localeCompare(b.label))

    if (languageOptions.length > 0) {
        fields.push({
            label: 'Language',
            value: 'creator_language',
            options: languageOptions,
        })
    }

    const countryOptions = contentFilterOptions.value.countries
        .map(code => ({ value: code, label: store.getters.getCountryFromCode(code) }))
        .sort((a, b) => a.label.localeCompare(b.label))

    if (countryOptions.length > 0) {
        fields.push({
            label: 'Country',
            value: 'creator_country',
            options: countryOptions,
        })
    }

    return fields
})

// Handle URL parameters for filtering
if (route.query['report.content.filters']) {
    filtering.value = []
    const urlParameterFilters = Array.isArray(route.query['report.content.filters'])
        ? route.query['report.content.filters']
        : [route.query['report.content.filters']]

    _.forEach(urlParameterFilters, (urlParameterFilter) => {
        const [id, value] = decodeURIComponent(urlParameterFilter).split(':')
        const values = value?.includes(',') ? value.split(',') : [value]
        filtering.value.push({
            id,
            value: values,
        })
    })
}

// Watch for changes and update URL
watch(
    () => [filtering],
    () => {
        router.push({
            query: {
                ...route.query,
                'report.content.filters': _.map(filtering.value, ({ id, value }) => {
                    return `${encodeURIComponent(id)}:${encodeURIComponent(value)}`
                }),
            },
        })
    },
    {
        immediate: false,
        deep: true,
    },
)

// Watch for changes and fetch data
watch(
    () => [
        props.reportId,
        props.platformId,
        props.platformIds,
        props.contentType,
        props.dayRange,
        pagination,
        sorting,
        toolbar,
        filtering,
    ],
    () => {
        // Clear selection when data changes
        selectedRows.value = {}
        fetchContent()
    },
    {
        immediate: false,
        deep: true,
    },
)

// Reset filters to defaults when content type or day range changes
watch(
    () => [props.contentType, props.dayRange],
    () => {
        // Reset to empty filters (show all content)
        filtering.value = []
    },
    {
        immediate: false,
    },
)

// Fetch tags when necessary parameters change
watch(() => [props.reportId, props.platformId, props.contentType, props.dayRange], () => {
    // Tag fetching logic would go here if needed
}, {
    immediate: true,
})

// Re-initialize enabled columns when platforms change
watch(() => [props.platformIds, props.platformId], () => {
    initEnabledColumns()
}, { immediate: false })

// Initialize enabled columns on mount
initEnabledColumns()

watch(() => [props.sponsoredDefault], () => {
        // If sponsoredDefault is not set or 'all', remove the is_sponsored filter
        if (!props.sponsoredDefault || props.sponsoredDefault === 'all') {
            filtering.value = filtering.value.filter(f => f.id !== 'is_sponsored')
            return
        }

        // Otherwise, set to the specific value
        const newFilterValue = [props.sponsoredDefault]
        let found = false
        _.forEach(filtering.value, filter => {
            if (filter.id === 'is_sponsored') {
                filter.value = newFilterValue
                found = true
            }
        })
        if (!found) {
            filtering.value.push({
                id : 'is_sponsored',
                value : newFilterValue,
            })
        }
    }, {
    immediate: true,
})

const handlePaginationUpdate = (newPagination) => {
    pagination.value.pageSize = newPagination.pageSize
    pagination.value.pageIndex = newPagination.pageIndex
}

const handleSortingUpdate = (newSorting) => {
    sorting.value = [...newSorting]
}

const handleTextFilterUpdate = (newTextFilterValue) => {
    toolbar.value.filterValue = newTextFilterValue
    pagination.value.pageIndex = 0
}

const handleFilteringUpdate = (newFilteringValue) => {
    filtering.value = [...newFilteringValue]
    pagination.value.pageIndex = 0
}

const debouncedTextFilterUpdate = _.debounce(handleTextFilterUpdate, 250)

// Watch for facet changes to refresh content
watch(
    () => [props.selectedFacetDimension, props.selectedFacetKey],
    () => {
        // Reset to first page when facet filter changes
        pagination.value.pageIndex = 0
        fetchContent()
    }
)
</script>

<template>
    <div>
        <DataTable
        :columns="columns"
        :data="data"
        :toolbar="toolbar"
        :is-loaded="isLoaded"
        :row-count="rowCount"
        :is-server-side="true"
        :pagination="pagination"
        :filtering="filtering"
        :hide-view-options="true"
        table-header-cell-class="first:pl-4 whitespace-nowrap"
        table-cell-class="px-3 py-0 text-sm data-[subrow=true]:bg-slate-100 h-[50px]"
        :preferences-id="`reportContent.${props.platformId}`"
        @update:pagination="handlePaginationUpdate"
        @update:sorting="handleSortingUpdate"
        @update:text-filter="debouncedTextFilterUpdate"
        @update:filtering="handleFilteringUpdate"
    >
        <!-- Column display manager replaces the default view options -->
        <template #toolbar-start="{ table }">
            <ColumnDisplayManager
                :available-columns="managerColumns"
                :groups="columnGroups"
                :model-value="enabledColumns"
                @update:model-value="onEnabledColumnChange"
            />
            <DateRangePicker
                :model-value="publishDateRange"
                :min-date="props.reportStartDate"
                :max-date="props.reportEndDate"
                placeholder="Publish Date"
                @update:model-value="handlePublishDateChange"
            />
            <DataTableFilterMenu
                :table="table"
                :filter-fields="filterFields"
            />
        </template>

        <!-- Bulk actions and Export button on the right side of toolbar -->
        <template #toolbar-actions>
            <!-- Bulk actions dropdown (only visible when items are selected) -->
            <DropdownMenu v-if="selectedCount > 0">
                <DropdownMenuTrigger as-child>
                    <button
                        class="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-1.5 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                        :disabled="isUpdatingSponsorship"
                    >
                        <MoreHorizontal class="mr-2 h-4 w-4" />
                        {{ selectedCount }} selected
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem
                        class="cursor-pointer gap-2"
                        :disabled="isUpdatingSponsorship"
                        @click="updateSponsorshipOverride('sponsored')"
                    >
                        <Check class="h-4 w-4 text-emerald-600" />
                        <span>Mark as Sponsored</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        class="cursor-pointer gap-2"
                        :disabled="isUpdatingSponsorship"
                        @click="updateSponsorshipOverride('organic')"
                    >
                        <X class="h-4 w-4 text-slate-600" />
                        <span>Mark as Organic</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        class="cursor-pointer gap-2"
                        :disabled="isUpdatingSponsorship"
                        @click="updateSponsorshipOverride(null)"
                    >
                        <RotateCcw class="h-4 w-4 text-amber-600" />
                        <span>Reset to Original</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <button
                class="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-4 py-1.5 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                @click="onExportContent"
            >
                <Download class="mr-2 h-4 w-4" />
                Export
            </button>
        </template>

        <template #loading-skeleton>
            <tr v-for="rowIndex in pagination.pageSize" :key="rowIndex">
                <td v-for="colIndex in columns?.length" :key="colIndex" class="h-[50px]">
                    <div class="my-0.5 h-full w-full animate-pulse rounded bg-slate-100" />
                </td>
            </tr>
        </template>
    </DataTable>
    </div>
</template>
